/**
 * AI に質問（ASK）
 *
 * - 質問を Cloudflare Worker 経由で Claude Sonnet 5.5 へ送信（教材全文を文脈として渡す）
 * - 回答には根拠にした章（最大 3）が付き、タップで記事へ
 * - 同期先が未設定なら案内と「設定を開く」、送信は無効
 * - 待機中はゾーンの点灯、失敗したら「もう一度送る」
 * - 履歴は最近 20 件（localStorage `zn.ask.history`）
 * - 23. シーンノート・露出画面から開くと、そのシーンの記録を添えて相談できる
 *
 * 認証は設定で保存した DEVICE_TOKEN を流用する。
 * AI の応答は外部入力と同等に扱い、必ずエスケープしてから表示する（CLAUDE.md S3-4）。
 */

import { showScreen, escapeHtml, toast, icon, barHtml, backBtn, ZONE_ROMAN } from './ui.js';
import { getSyncConfig, isSyncConfigured } from './sync.js';
import { getLearnContentAsMarkdown } from './search.js';
import { ARTICLES } from './data/learn-content.js';
import { buildSceneContext } from './scene-context.js';

const HISTORY_KEY = 'zn.ask.history';
const HISTORY_LIMIT = 20;
const MAX_LEN = 500;

let state = { status: 'idle', shown: null, lastQuestion: '', error: '' };
let attached = null; // 23. 相談するシーン { id, title, context }

const SCENE_QUESTIONS = [
  'この露出と現像方針で良いですか？',
  'ハイライトとシャドウの置き方を見直すなら？',
  '現像やプリントの結果から、次に変えることは？',
];

/**
 * @param {{ question?: string, scene?: object }} opts
 *   scene：相談するシーン（シーンノート・露出画面の「このシーンを AI に相談」から）
 */
export function openAsk(opts = {}) {
  state = { status: 'idle', shown: null, lastQuestion: '', error: '' };
  attached = opts.scene ? { id: opts.scene.id, title: opts.scene.title || '無題のシーン', context: buildSceneContext(opts.scene) } : null;
  render(opts.question || '');
  showScreen('ask');
}

function render(initialQ) {
  const el = document.getElementById('screen-ask');
  const configured = isSyncConfigured();
  el.innerHTML = `
    ${barHtml(backBtn())}
    <div class="page-head"><div><div class="eyebrow">ask</div><div class="page-title">AI に質問</div></div></div>
    <div class="body" id="ask-body">
      <p class="sub" style="margin:0 var(--gutter);font-size:13px;line-height:1.7">教材 ${ARTICLES.length} 章を読み込んだ AI が答えます。回答には根拠にした章へのリンクが付きます。</p>
      ${configured ? '' : `
        <div style="margin:16px 16px 0;box-shadow:inset 0 0 0 1px var(--color-ink);padding:16px;animation:zn-up .4s both">
          <div style="font:500 15px/1.4 var(--font-jp-sans)">クラウド連携が未設定です</div>
          <p class="sub" style="margin:6px 0 0;font-size:13px;line-height:1.7">設定 › クラウド同期 で Cloudflare Worker の URL とトークンを入力すると使えます。</p>
          <button class="btn-line" type="button" data-settings style="margin-top:12px;height:44px">設定を開く</button>
        </div>`}
      ${attached ? `
        <div style="margin:16px 16px 0;padding:12px 14px;background:var(--color-grain);animation:zn-up .4s both" id="ask-scene">
          <div style="display:flex;align-items:center;gap:10px">
            <div style="flex:1;min-width:0"><div class="field__label" style="margin:0;font-size:11px">相談するシーン</div><div style="font:500 15px/1.4 var(--font-jp-serif);margin-top:4px">${escapeHtml(attached.title)}</div></div>
            <button type="button" class="sub" data-detach style="font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">外す</button>
          </div>
          <p class="sub" style="font-size:11.5px;line-height:1.6;margin:6px 0 0">測光ピン・露出・現像・プリントの記録を文字で添えて送ります（写真と位置情報は送りません）。</p>
          <div class="chips" style="padding:8px 0 0">${SCENE_QUESTIONS.map((q) => `<button type="button" class="chip" data-sq="${escapeHtml(q)}" style="height:32px;background:var(--color-paper)">${escapeHtml(q)}</button>`).join('')}</div>
        </div>` : ''}
      <div class="ask-box" style="opacity:${configured ? 1 : 0.45}">
        <textarea id="ask-input" maxlength="${MAX_LEN}" placeholder="例：曇天で N+1 にするか迷うときの判断基準は？" ${configured ? '' : 'disabled'}>${escapeHtml(initialQ)}</textarea>
        <div class="ask-box__foot">
          <span class="en sub" style="flex:1;font-size:11px;line-height:1.4"><span id="ask-count">0</span> / ${MAX_LEN} · Claude Sonnet 5.5 · <span style="font-family:var(--font-jp-sans)">平均 5〜15 秒</span></span>
          <button class="ask-send" type="button" id="ask-send" disabled>送信</button>
        </div>
      </div>
      <div id="ask-result"></div>
      <div id="ask-history"></div>
      <div style="height:40px"></div>
    </div>
  `;
  el.querySelector('[data-settings]')?.addEventListener('click', () => import('./settings.js').then((m) => m.openSettings()));
  const input = el.querySelector('#ask-input');
  const send = el.querySelector('#ask-send');
  const sync = () => {
    el.querySelector('#ask-count').textContent = String(input.value.length);
    send.disabled = !configured || !input.value.trim() || state.status === 'loading';
  };
  input.addEventListener('input', sync);
  el.querySelector('[data-detach]')?.addEventListener('click', () => { attached = null; el.querySelector('#ask-scene')?.remove(); });
  el.querySelectorAll('[data-sq]').forEach((b) => b.addEventListener('click', () => { input.value = b.dataset.sq; sync(); input.focus(); }));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(input.value); }
  });
  send.addEventListener('click', () => submit(input.value));
  sync();
  renderResult();
  renderHistory();
}

async function submit(raw) {
  const question = String(raw || '').trim();
  if (!question) { toast('質問を入力してください'); return; }
  if (question.length > MAX_LEN) { toast(`質問は ${MAX_LEN} 文字以内で`); return; }
  if (!isSyncConfigured()) { toast('設定で Cloudflare Worker を設定してください'); return; }
  if (state.status === 'loading') return;
  state = { status: 'loading', shown: null, lastQuestion: question, error: '' };
  renderResult();
  const send = document.getElementById('ask-send');
  if (send) send.disabled = true;
  try {
    const { endpoint, token, deviceId } = getSyncConfig();
    const res = await fetch(`${endpoint.replace(/\/$/, '')}/api/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Device-Id': deviceId },
      body: JSON.stringify({ question, learnContent: getLearnContentAsMarkdown(), ...(attached ? { sceneContext: attached.context } : {}) }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const item = { question, answer: String(data.answer || ''), references: Array.isArray(data.references) ? data.references.slice(0, 3) : [], at: Date.now(), ...(attached ? { sceneTitle: attached.title } : {}) };
    pushHistory(item);
    state = { status: 'answer', shown: item, lastQuestion: question, error: '' };
    const input = document.getElementById('ask-input');
    if (input) { input.value = ''; input.dispatchEvent(new Event('input')); }
  } catch (e) {
    console.error('[ask] failed:', e);
    state = { status: 'error', shown: null, lastQuestion: question, error: e.message };
  }
  renderResult();
  renderHistory();
}

function renderResult() {
  const box = document.getElementById('ask-result');
  if (!box) return;
  if (state.status === 'loading') {
    box.innerHTML = `<div style="margin:28px var(--gutter) 0">
      <div class="zlights zlights--sm">${ZONE_ROMAN.map((_, i) => `<span class="zc-${i}" style="animation-delay:${i * 95}ms"></span>`).join('')}</div>
      <div style="font:500 15px/1 var(--font-jp-sans);margin-top:14px">考えています…</div></div>`;
    return;
  }
  if (state.status === 'error') {
    box.innerHTML = `<div style="margin:24px var(--gutter) 0;border-top:1px solid var(--color-ink);padding-top:14px;animation:zn-up .4s both">
      <div style="font:500 17px/1.4 var(--font-jp-serif)">回答を取得できませんでした</div>
      <p class="sub" style="margin:6px 0 0;font-size:13px;line-height:1.7">通信状況や設定の URL・トークンを確認してください。（${escapeHtml(state.error)}）</p>
      <button class="btn-line" type="button" data-retry style="margin-top:12px">${icon('reset', 16)}もう一度送る</button></div>`;
    box.querySelector('[data-retry]').addEventListener('click', () => submit(state.lastQuestion));
    return;
  }
  const item = state.shown;
  if (!item) { box.innerHTML = ''; return; }
  const refs = (item.references || []).map((r) => {
    const id = r.articleId || r.id || '';
    const a = ARTICLES.find((x) => x.id === id);
    return a ? { id, chapter: a.chapter, title: a.title } : null;
  }).filter(Boolean);
  box.innerHTML = `
    <div style="margin:24px var(--gutter) 0;border-top:1px solid var(--color-ink);padding-top:14px;animation:zn-up .5s both">
      <div class="eyebrow">answer</div>
      ${item.sceneTitle ? `<div class="sub" style="font-size:12px;margin-top:8px">シーン「${escapeHtml(item.sceneTitle)}」について</div>` : ''}
      <div class="ask-q" style="font:500 16px/1.6 var(--font-jp-serif);margin-top:10px">${escapeHtml(item.question)}</div>
      <div class="ask-a">${formatAnswerBody(item.answer)}</div>
      ${refs.length ? `<div class="field__label" style="margin:16px 0 8px">参照した章</div>
        ${refs.map((r) => `<button class="ref-row" type="button" data-ref="${escapeHtml(r.id)}"><span class="en sub" style="width:22px;font-size:12px">${escapeHtml(r.chapter)}</span><span style="flex:1">${escapeHtml(r.title)}</span>${icon('chev', 16)}</button>`).join('')}` : ''}
    </div>`;
  box.querySelectorAll('[data-ref]').forEach((b) => b.addEventListener('click', () => {
    import('./learn.js').then((m) => m.openLearnArticle(b.dataset.ref));
  }));
}

function renderHistory() {
  const box = document.getElementById('ask-history');
  if (!box) return;
  const hist = loadHistory();
  if (!hist.length) { box.innerHTML = ''; return; }
  box.innerHTML = `
    <div style="margin:28px var(--gutter) 0;border-top:1px solid var(--color-ink);padding-top:14px">
      <div class="eyebrow" style="display:flex;justify-content:space-between"><span>history</span><span class="sec-head__jp">最近 ${HISTORY_LIMIT} 件</span></div>
      ${hist.map((h, i) => `<button class="hist-row" type="button" data-h="${i}"><span style="font:400 14px/1.5 var(--font-jp-sans)">${escapeHtml(h.question)}</span><span class="en sub" style="font-size:11px;flex:none">${escapeHtml(new Date(h.at).toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' }))}</span></button>`).join('')}
    </div>`;
  box.querySelectorAll('[data-h]').forEach((b) => b.addEventListener('click', () => {
    const item = hist[Number(b.dataset.h)];
    if (!item) return;
    state = { status: 'answer', shown: item, lastQuestion: item.question, error: '' };
    const input = document.getElementById('ask-input');
    if (input && !input.disabled) { input.value = item.question; input.dispatchEvent(new Event('input')); }
    renderResult();
    document.getElementById('ask-result')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }));
}

/** 改行と簡易マークダウン（太字・コード）。必ずエスケープしてから変換する */
function formatAnswerBody(text) {
  let html = escapeHtml(text || '');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  return html.replace(/\n/g, '<br/>');
}

function loadHistory() {
  try {
    const arr = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function pushHistory(item) {
  const arr = loadHistory();
  arr.unshift(item);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(arr.slice(0, HISTORY_LIMIT)));
}
